import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { createSelector } from "reselect";
import { TempoElement } from "@/utils/tempoElement";
import { STORYBOARD_HYDRATION_STATUS } from "@/utils/constants";
import { track } from "@/utils/events/tracker";
import { useCanvasStore } from "./zustand/canvasStore";
import { RootState } from "./store";

export enum IFRAME_STATE {
  LOADING = "LOADING",
  SHOWING = "SHOWING",
  HIDDEN = "HIDDEN",
}

// Matches the interface in devtools
export enum FIXED_IFRAME_MESSAGE_IDS {
  HOVERED_ELEMENT_KEY = "HOVERED_ELEMENT_KEY",
  SELECTED_ELEMENT_KEY = "SELECTED_ELEMENT_KEY",
  MULTI_SELECTED_ELEMENT_KEYS = "MULTI_SELECTED_ELEMENT_KEYS",
  CONTEXT_REQUESTED = "CONTEXT_REQUESTED",
  WHEEL_EVENT = "WHEEL_EVENT",
  NAV_TREE = "NAV_TREE",
  PROCESSED_CSS_RULES_FOR_ELEMENT = "PROCESSED_CSS_RULES_FOR_ELEMENT",
  CSS_EVALS_FOR_ELEMENT = "CSS_EVALS_FOR_ELEMENT",
  ELEMENT_CLASS_LIST = "ELEMENT_CLASS_LIST",
  KEY_DOWN_EVENT = "KEY_DOWN_EVENT",
  KEY_UP_EVENT = "KEY_UP_EVENT",
  MOUSE_MOVE_EVENT = "MOUSE_MOVE_EVENT",
  DRAG_START_EVENT = "DRAG_START_EVENT",
  DRAG_END_EVENT = "DRAG_END_EVENT",
  DRAG_CANCEL_EVENT = "DRAG_CANCEL_EVENT",
  LATEST_HREF = "LATEST_HREF",
  LATEST_HYDRATION_ERROR_STATUS = "LATEST_HYDRATION_ERROR_STATUS",
  START_EDITING_TEXT = "START_EDITING_TEXT",
  EDITED_TEXT = "EDITED_TEXT",
  INSTANT_UPDATE_DONE = "INSTANT_UPDATE_DONE",
  EDIT_DYNAMIC_TEXT = "EDIT_DYNAMIC_TEXT",
  ERROR = "ERROR",
}
const FIXED_IFRAME_MESSAGE_ID_VALUES = new Set(
  Object.values(FIXED_IFRAME_MESSAGE_IDS)
);

export interface IFrameMessage {
  id: string;
  type: string;
  payload: any;

  storyboardId?: string; // If set, sends to a particular t-frame
  sent?: boolean;
  response?: any;
}

export const iframeSlice = createSlice({
  name: "iframe",
  initialState: {
    iframeState: IFRAME_STATE.LOADING,
    forceReloadKey: 0, // Used to force reload the iframe
    iframeChangedKey: 0, // Doesn't reload the iframe, but notifies that it is likely that it changed

    // Send messages to iframe from anywhere
    iFrameMessageQueue: [],
    iFrameMessageResponses: {}, // ID -> response

    // Dev server info
    canvasIdToDevServerUrl: {},

    // Specific known iframe message states
    hoveredElementKey: null,
    selectedElementKey: null,

    multiSelectedElementKeys: null, // In multiselect mode this is an array and contains the selected element key as one of the elements

    showContextMenu: false,
    requestedContextPosition: null,

    // For requesting a codebase ID be set
    pendingSelectionCodebaseId: null,

    processedCssRules: null,
    cssEvals: null,
    elementClassList: null,

    // Files that have been modified and need to be reloaded
    filesToReload: {},

    // TODO: we want to remove this when we figure out how to trigger drop events on the iframe
    draggingComponent: false,

    // Fetched from the iframe
    storyboardIdToHref: {},

    // Storyboard internal error state (not compile errors)
    storyboardIdToInternalError: {},

    // Extracted for instant updates/copy/paste
    multiselectedElementHTML: null,
    selectedElementHTML: null,
    extractedComponentFile: null,
    extractedComponentFilePath: null,
  },
  reducers: {
    forceReloadIFrame: (state) => {
      state.forceReloadKey = state.forceReloadKey + 1;
      state.iframeState = IFRAME_STATE.LOADING;
    },
    setIFrameState: (state, action) => {
      state.iframeState = action.payload;
    },
    setIFrameChanged: (state) => {
      state.iframeChangedKey = state.iframeChangedKey + 1;
    },

    setHoveredElementKey: (state, action) => {
      if (action.payload instanceof TempoElement) {
        // @ts-expect-error TS2322
        state.hoveredElementKey = action.payload.getKey();
        return;
      }

      state.hoveredElementKey = action.payload;
    },
    setSelectedElementKey: (state, action) => {
      // If this is called always reset the multi-select state as we are selecting a single element
      state.multiSelectedElementKeys = null;

      if (action.payload instanceof TempoElement) {
        // @ts-expect-error TS2322
        state.selectedElementKey = action.payload.getKey();
        return;
      }

      state.selectedElementKey = action.payload;
    },
    setMultiSelectWithElementKey: (state, action) => {
      let newSelectedElementKey = action.payload;
      if (newSelectedElementKey instanceof TempoElement) {
        newSelectedElementKey = newSelectedElementKey.getKey();
      }

      // Case 1: if there is nothing selected, just select this element
      if (!state.selectedElementKey) {
        state.selectedElementKey = newSelectedElementKey;
        return;
      }

      let newMultiselectedElementKeys: string[] = [];

      const newSelectedElement = TempoElement.fromKey(newSelectedElementKey);
      const currentSelectedElement = TempoElement.fromKey(
        state.selectedElementKey
      );

      // Case 2: there is something to deselect from the multiselect keys
      const elementToDeselect = state.multiSelectedElementKeys
        // @ts-expect-error TS2339
        ?.map((elementKey: string) => {
          return TempoElement.fromKey(elementKey);
        })
        .find((element: TempoElement) => {
          return (
            element.isParentOf(newSelectedElement) ||
            element.isEqual(newSelectedElement)
          );
        });
      if (elementToDeselect) {
        // @ts-expect-error TS2339
        newMultiselectedElementKeys = state.multiSelectedElementKeys?.filter(
          (elementKey) =>
            !elementToDeselect.isEqual(TempoElement.fromKey(elementKey))
        );

        // If deselecting the main selected element, pick a new one to be the main selected element
        // Note that if there is only 1 element left there is logic further down to handle that case
        if (
          elementToDeselect.getKey() === state.selectedElementKey &&
          newMultiselectedElementKeys.length > 1
        ) {
          // @ts-expect-error TS2322
          state.selectedElementKey = newMultiselectedElementKeys[0];
        }

        // Case 3: This element is a sibling of the currently selected element
      } else if (newSelectedElement.isSiblingOf(currentSelectedElement)) {
        // @ts-expect-error TS2339
        if (state.multiSelectedElementKeys?.length) {
          newMultiselectedElementKeys = [
            ...state.multiSelectedElementKeys,
            newSelectedElementKey,
          ];
        } else {
          newMultiselectedElementKeys = [
            currentSelectedElement.getKey(),
            newSelectedElementKey,
          ];
        }
      } else {
        // Just return, since this is not an element that can be added or removed from multiselect
        return;
      }

      if (newMultiselectedElementKeys.length > 1) {
        // @ts-expect-error TS2322
        state.multiSelectedElementKeys = newMultiselectedElementKeys;
        return;
      }

      // Now handle the new multiselected keys after changes
      // If only one left in multiselect mode, just select that one and exit the multiselect mode
      if (newMultiselectedElementKeys.length === 1) {
        // @ts-expect-error TS2322
        state.selectedElementKey = newMultiselectedElementKeys[0];
        state.multiSelectedElementKeys = null;
        return;
      }

      // Empty case, just exit, shouldn't happen but here just in case
      state.multiSelectedElementKeys = null;
    },
    setAllSelectedElementKeys: (state, action) => {
      state.multiSelectedElementKeys = action.payload.multiSelectedElementKeys;
      state.selectedElementKey = action.payload.selectedElementKey;
    },
    setShowContextMenu: (state, action) => {
      state.showContextMenu = action.payload;
    },

    setRequestedContextPosition: (state, action) => {
      state.requestedContextPosition = action.payload;
    },
    setPendingSelectionCodebaseId: (state, action) => {
      state.pendingSelectionCodebaseId = action.payload;
    },

    // These are for window.postMessages to the iframe
    sendMessageToIFrame: (state, action) => {
      const message = {
        id: action.payload.id || uuidv4(),
        type: action.payload.type,
        payload: action.payload.payload,
        storyboardId: action.payload.storyboardId,
      };

      // @ts-expect-error TS2345
      state.iFrameMessageQueue.push(message);
    },
    setMessageSent: (state, action) => {
      // @ts-expect-error TS2532
      state.iFrameMessageQueue.find(
        // @ts-expect-error TS2339
        (message) => message.id === action.payload.id
      ).sent = true;
    },
    setMessageResponse: (state, action) => {
      if (!action.payload.id) {
        return;
      }

      const storyboardId = action.payload.storyboardId;

      // If this is a fixed message, just set the exact state
      if (FIXED_IFRAME_MESSAGE_ID_VALUES.has(action.payload.id)) {
        switch (action.payload.id) {
          case FIXED_IFRAME_MESSAGE_IDS.HOVERED_ELEMENT_KEY:
            state.hoveredElementKey = action.payload.elementKey;
            break;
          case FIXED_IFRAME_MESSAGE_IDS.SELECTED_ELEMENT_KEY:
            // This message ID is used to send other info as well,
            // sometimes we don't want to re-set the selected element key
            if (action.payload.doNotSetElementKey) {
              return;
            }

            state.selectedElementKey = action.payload.elementKey;
            break;
          case FIXED_IFRAME_MESSAGE_IDS.MULTI_SELECTED_ELEMENT_KEYS:
            // This message ID is used to send other info as well,
            // sometimes we don't want to re-set the selected element key
            if (action.payload.doNotSetElementKeys) {
              return;
            }

            state.multiSelectedElementKeys = action.payload.elementKeys;
            break;
          case FIXED_IFRAME_MESSAGE_IDS.CONTEXT_REQUESTED:
            const position = {
              callsite: "iframe",
              event: action.payload.event,
            };
            state.showContextMenu = true;
            // @ts-expect-error TS2322
            state.requestedContextPosition = position;
            break;

          case FIXED_IFRAME_MESSAGE_IDS.PROCESSED_CSS_RULES_FOR_ELEMENT:
            if (!state.selectedElementKey) {
              return;
            }

            state.processedCssRules = action.payload.processedCssRules;
            break;
          case FIXED_IFRAME_MESSAGE_IDS.CSS_EVALS_FOR_ELEMENT:
            if (!state.selectedElementKey) {
              return;
            }

            state.cssEvals = action.payload.cssEvals;
            break;
          case FIXED_IFRAME_MESSAGE_IDS.ELEMENT_CLASS_LIST:
            if (!state.selectedElementKey) {
              return;
            }
            state.elementClassList = action.payload.classList;
            break;
          case FIXED_IFRAME_MESSAGE_IDS.LATEST_HREF:
            state.storyboardIdToHref[storyboardId] = action.payload.href;
            break;
          case FIXED_IFRAME_MESSAGE_IDS.LATEST_HYDRATION_ERROR_STATUS:
            const messagesToSkip = [
              "Uncaught TypeError: Cannot read properties of null (reading 'useState')",
              "Uncaught TypeError: Cannot read properties of null (reading 'useContext')",
              "Uncaught TypeError: Cannot read properties of null (reading 'useMemo')",
              "Uncaught TypeError: Cannot read properties of null (reading 'useRef')",
            ];

            const skipErrorMessage =
              action.payload.errorBody &&
              messagesToSkip.some((message) => {
                return action.payload.errorBody.includes(message);
              });

            if (skipErrorMessage) {
              track("STORYBOARD_SKIP_ERROR", {
                storyboard_id: storyboardId,
                error: action.payload,
                type: "hydration",
              });
              return;
            }

            if (
              action.payload.status !== STORYBOARD_HYDRATION_STATUS.NO_ERROR
            ) {
              track("STORYBOARD_ERROR", {
                storyboard_id: storyboardId,
                error: action.payload,
                type: "hydration",
              });
            }

            state.storyboardIdToInternalError[storyboardId] = {
              status: action.payload.status,
              descr: action.payload.errorDescr,
              label: action.payload.errorLabel,
              body: action.payload.errorBody,
            };
            break;
        }

        return;
      }

      let message = state.iFrameMessageQueue.find(
        // @ts-expect-error TS2339
        (message) => message.id === action.payload.id
      );

      // Sometimes the iframe sends a response without any messages that the app cares about
      if (!message) {
        // @ts-expect-error TS2322
        message = {
          id: action.payload.id,
          storyboardId,
          type: "RESPONSE_ONLY",
        };
      }

      // @ts-expect-error TS18048
      message.response = action.payload;
      state.iFrameMessageResponses[action.payload.id] = message;
    },

    setDevServerUrl: (state, action) => {
      state.canvasIdToDevServerUrl[action.payload.canvasId] =
        action.payload.url;
    },
    setDraggingComponent: (state, action) => {
      state.draggingComponent = action.payload;
    },
    setSelectedElementHTML: (state, action) => {
      state.selectedElementHTML = action.payload;
    },
    setMultiSelectedElementHTML: (state, action) => {
      state.multiselectedElementHTML = action.payload;
    },
    setExtractedComponentFile: (state, action) => {
      state.extractedComponentFile = action.payload;
    },
    setExtractedComponentFilePath: (state, action) => {
      state.extractedComponentFilePath = action.payload;
    },
    clearInternalErrorForSb: (state, action) => {
      const newErrorObj = {
        ...state.storyboardIdToInternalError,
      };
      delete newErrorObj[action.payload.storyboardId];
      state.storyboardIdToInternalError = newErrorObj;
    },
    clearAllInternalErrors: (state) => {
      state.storyboardIdToInternalError = {};
    },
  },
});

/**
 * Custom memoized selectors for the tempo element keys
 */

const createTempoElementSelector = (keySelector) =>
  createSelector([keySelector], (key) => {
    if (key) {
      return TempoElement.fromKey(key);
    }
    return TempoElement.empty();
  });

const selectHoveredElementKey = (state: RootState) =>
  state.iframe.hoveredElementKey;
const selectSelectedElementKey = (state: RootState) =>
  state.iframe.selectedElementKey;
const selectMultiSelectedElementKeys = (state: RootState) =>
  state.iframe.multiSelectedElementKeys;

export const selectHoveredTempoElement = createTempoElementSelector(
  selectHoveredElementKey
);
export const selectSelectedTempoElement = createTempoElementSelector(
  selectSelectedElementKey
);
export const selectMultiSelectedTempoElements = createSelector(
  [selectMultiSelectedElementKeys],
  (keys) => {
    return keys
      ? // @ts-expect-error TS2339
        keys.map((key) =>
          key ? TempoElement.fromKey(key) : TempoElement.empty()
        )
      : null;
  }
);

// Action creators are generated for each case reducer function
export const {
  setIFrameState,
  setIFrameChanged,
  forceReloadIFrame,
  sendMessageToIFrame,
  setMessageSent,
  setMessageResponse,
  setDevServerUrl,
  setHoveredElementKey,
  setSelectedElementKey,
  setMultiSelectWithElementKey,
  setShowContextMenu,
  setRequestedContextPosition,
  setPendingSelectionCodebaseId,
  setDraggingComponent,
  setAllSelectedElementKeys,
  setSelectedElementHTML,
  setMultiSelectedElementHTML,
  setExtractedComponentFile,
  setExtractedComponentFilePath,
  clearInternalErrorForSb,
  clearAllInternalErrors,
} = iframeSlice.actions;

export default iframeSlice.reducer;
